import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { ArrowIcon, Button as Button95, Checkbox, Hourglass, NumberInput, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import { BasicScrollView } from "@/components/BasicScrollView";
import Head from 'expo-router/head';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import Footer from '@/components/Footer';
import { CustomDivider } from "@/components/CustomDivider";
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as UIUtils from '@/utilities/UIUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller, Resolver } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { UserInfo } from '@/types/user_info';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
  alignLeft: {
    alignSelf: 'flex-start',
  },
  checkboxWithIcon: {
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
});

const SUBSCRIPTION_DESCRIPTIONS = new Map<string, string>([
  ["weeklymatches", "Weekly emails with some profiles you haven't decided on yet"],
  ["dailymatches", "Daily emails with some profiles you haven't decided on yet"],
  ["marketing", "Marketing messages about Rabbl"],
  ["newmatch", "New matches"],
  ["newchatmessage", "New chat messages"],
  ["collabs", "Collaboration messages with other Rabbl owners"],
  ["rabblwelcomes", "Welcome message when joining a Rabbl"],
]);

export default function ManageSubscriptions() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const dispatch = useDispatch();
  const [ user_info, set_user_info] = useState<UserInfo | undefined>(undefined);
  const { id, unsubscribe } = useLocalSearchParams<{ id?: string, unsubscribe?: string }>();
  const [error, setError] = useState<string>("");
  const [processing, setProcessing] = useState(false);
  const [updated, setUpdated] = useState<string>("");

  const [pref_notify_daily_email, setpref_notify_daily_email] = useState(false);
  const [pref_notify_weekly_email, setpref_notify_weekly_email] = useState(true);
  const [pref_marketing_messages, setpref_marketing_messages] = useState(true);
  const [pref_match_messages, setpref_match_messages] = useState(true);
  const [pref_collab_messages, setpref_collab_messages] = useState(true);
  const [pref_welcome_messages, setpref_welcome_messages] = useState(true);
  const [pref_chat_messages, setpref_chat_messages] = useState(true);

  const handleResponse = useCallback((response: any) => {
    const responseUserInfo: UserInfo = ((response as any).body as any).user_info;
    setpref_notify_daily_email(responseUserInfo.pref_notify_daily_email ?? false);
    setpref_notify_weekly_email(responseUserInfo.pref_notify_weekly_email ?? true);
    setpref_marketing_messages(responseUserInfo.pref_marketing_messages ?? true);
    setpref_collab_messages(responseUserInfo.pref_collab_messages ?? true);
    setpref_chat_messages(responseUserInfo.pref_chat_messages ?? true);
    setpref_match_messages(responseUserInfo.pref_match_messages ?? true);
    setpref_welcome_messages(responseUserInfo.pref_welcome_messages ?? true);
    set_user_info(responseUserInfo);
    dispatch(setData(response));
  }, []);

  useEffect(() => {
    if (id) {
      const loadInitialData = async () => {
        try {
          showSpinner();
          setProcessing(true);
          const response = await AppUtilities.fetchUnauthorized(
            "ManageSubscriptions.loadInitialData",
            "/rabbl/api/subscriptions",
            "POST",
            {
              id: id,
              unsubscribe: unsubscribe,
            }
          );
          handleResponse(response);
          if (unsubscribe && unsubscribe.length > 0) {
            setUpdated(unsubscribe);
          }

        } catch (e) {
          setError(AppUtilities.getErrors(e));
          //AppUtils.showErrorToast(AppUtilities.getErrors(e), () => loadInitialData(), true);
          //AppUtilities.handleException(e, "ManageSubscriptions.loadInitialData");
        } finally {
          setProcessing(false);
          hideSpinner();
        }
      };

      loadInitialData();
    }
  }, [id, unsubscribe]);

  const onSubmit = useCallback(async() => {
    try {
      showSpinner();
      setProcessing(true);
      setUpdated("");

      const response = await AppUtilities.fetchAuthorized(
        "Preferences.onSubmit",
        "/rabbl/api/subscriptions",
        "POST",
        {
          id: id,
          pref_notify_daily_email: pref_notify_daily_email,
          pref_notify_weekly_email: pref_notify_weekly_email,
          pref_marketing_messages: pref_marketing_messages,
          pref_match_messages: pref_match_messages,
          pref_collab_messages: pref_collab_messages,
          pref_welcome_messages: pref_welcome_messages,
          pref_chat_messages: pref_chat_messages,
        }
      );

      handleResponse(response);
      AppUtils.showInformationalToast("Subscriptions saved");

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(), true);
      AppUtilities.handleException(e, "Preferences.onSubmit");
    } finally {
      setProcessing(false);
      hideSpinner();
    }
  },
  [
    id,
    pref_notify_daily_email,
    pref_notify_weekly_email,
    pref_marketing_messages,
    pref_match_messages,
    pref_collab_messages,
    pref_welcome_messages,
    pref_chat_messages,
  ]);

  const unsubscribeAll = useCallback(async () => {
    setpref_notify_weekly_email(false);
    setpref_notify_daily_email(false);
    setpref_marketing_messages(false);
    setpref_match_messages(false);
    setpref_collab_messages(false);
    setpref_welcome_messages(false);
    setpref_chat_messages(false);
    try {
      showSpinner();
      setProcessing(true);
      setUpdated("");

      const response = await AppUtilities.fetchAuthorized(
        "Preferences.unsubscribeAll",
        "/rabbl/api/subscriptions",
        "POST",
        {
          id: id,
          pref_notify_daily_email: false,
          pref_notify_weekly_email: false,
          pref_marketing_messages: false,
          pref_match_messages: false,
          pref_collab_messages: false,
          pref_welcome_messages: false,
          pref_chat_messages: false,
        }
      );

      handleResponse(response);
      AppUtils.showInformationalToast("Subscriptions saved");

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => unsubscribeAll(), true);
      AppUtilities.handleException(e, "Preferences.unsubscribeAll");
    } finally {
      setProcessing(false);
      hideSpinner();
    }
  }, [id]);

  if (!id || id.length == 0) {
    return UIUtils.getBasicPaneledErrorView("Manage subscriptions", "Missing subscription identifier");
  }

  if (error) {
    return UIUtils.getBasicPaneledErrorView("Error", error);
  }

  if (!user_info) {
    return null;
  }

  return (
    <BasicScrollView center={true}>
      <Meta title={"Manage subscriptions"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <AppText>Subscriptions for <AppText style={{ fontWeight: "bold" }}>{user_info.email}</AppText></AppText>

          { updated && updated.length > 0 ?
                <>
                  <CustomDivider fullWidth={true} />

                  <AppText><AppText style={{ fontWeight: "bold", color: "green" }}>Successfully unsubscribed from</AppText>: {SUBSCRIPTION_DESCRIPTIONS.get(updated)}</AppText>
                </>
              :
                null
          }

          <View style={styles.buttonWrapper}>
            <Button95 onPress={unsubscribeAll} disabled={processing}>Unsubscribe from everything</Button95>
          </View>

          <CustomDivider fullWidth={true} />

          <CustomText>All subscriptions</CustomText>

          <AppText>Change any subscriptions and then press <AppText style={{ fontWeight: "bold" }}>Save</AppText> below</AppText>

          <CustomText style={CustomTextStyles.medium}>Rabbl Users</CustomText>

          <Checkbox
            status={pref_notify_weekly_email ? 'checked' : 'unchecked'}
            onPress={() => setpref_notify_weekly_email(pref_notify_weekly_email ? false : true)}
            label={SUBSCRIPTION_DESCRIPTIONS.get("weeklymatches")}
          />

          <Checkbox
            status={pref_match_messages ? 'checked' : 'unchecked'}
            onPress={() => setpref_match_messages(pref_match_messages ? false : true)}
            label={SUBSCRIPTION_DESCRIPTIONS.get("newmatch")}
          />

          <Checkbox
            status={pref_chat_messages ? 'checked' : 'unchecked'}
            onPress={() => setpref_chat_messages(pref_chat_messages ? false : true)}
            label={SUBSCRIPTION_DESCRIPTIONS.get("newchatmessage")}
          />

          <Checkbox
            status={pref_marketing_messages ? 'checked' : 'unchecked'}
            onPress={() => setpref_marketing_messages(pref_marketing_messages ? false : true)}
            label={SUBSCRIPTION_DESCRIPTIONS.get("marketing")}
          />

          <Checkbox
            status={pref_welcome_messages ? 'checked' : 'unchecked'}
            onPress={() => setpref_welcome_messages(pref_welcome_messages ? false : true)}
            label={SUBSCRIPTION_DESCRIPTIONS.get("rabblwelcomes")}
          />

          <CustomDivider fullWidth={true} />

          <CustomText style={CustomTextStyles.medium}>Rabbl Owners</CustomText>

          <Checkbox
            status={pref_collab_messages ? 'checked' : 'unchecked'}
            onPress={() => setpref_collab_messages(pref_collab_messages ? false : true)}
            label={SUBSCRIPTION_DESCRIPTIONS.get("collabs")}
          />

          <CustomDivider fullWidth={true} />

          <View style={styles.buttonWrapper}>
            <Button95 onPress={onSubmit} disabled={processing}>Save</Button95>
          </View>
        </View>
      </Panel>
      
      <Footer />

    </BasicScrollView>
  );
}
